import moment from "moment-timezone";
import Decimal from "decimal.js";
import { Card } from "@/components/ui/card";
import { BirthdayFundRequest } from "@/lib/api/fund";
import { CommonDestinationTimezone, formatCurrency } from "@/lib/utils";
import { DataTable, type DataTableColumn, dateWithFormatSort } from "@/components/ui/DataTable/index";
import { sum, sumDecimal } from "@/components/ui/DataTable/aggregation/index";

const sumAggregation = sum as any;
const sumDecimalAggregation = sumDecimal as any;

function getMonth(r: BirthdayFundRequest): string {
  const date = r.granted_date || r.created_at;
  if (!date) return "Unknown";
  return moment.tz(date, CommonDestinationTimezone).toISOString();
}

function formatMonth(month: string): string {
  if (month === "Unknown") return "Unknown";
  return moment.tz(month, CommonDestinationTimezone).format("MMMM YYYY");
}

type MonthByLocationRow = {
  month: string;
  party_location: string;
  was_requested: number;
  was_granted: number;
  amount_granted_numeric: number;
};

type MonthByPartnerRow = {
  month: string;
  nominating_partner: string;
  was_requested: number;
  was_granted: number;
  amount_granted_numeric: number;
};

export function ByMonthSummary({ requests }: { requests: BirthdayFundRequest[] }) {
  const data: (MonthByLocationRow & MonthByPartnerRow)[] = requests.map((r) => ({
    month: getMonth(r),
    party_location: r.survey?.data?.party_location || "Unknown",
    nominating_partner: r.organization || r.nominated_organization || "Unknown",
    was_requested: 1,
    was_granted: (r.has_been_granted && new Decimal(r.amount_granted).gt(0)) ? 1 : 0,
    amount_granted_numeric: r.amount_granted ? new Decimal(r.amount_granted).toNumber() : 0,
  }));

  const locationColumns: DataTableColumn<MonthByLocationRow>[] = [
    {
      id: "month",
      accessorKey: "month",
      header: "Month",
      accessorFn: (row) => moment.tz(row.month as string, CommonDestinationTimezone).format("MMM YYYY"),
      sortingFn: dateWithFormatSort("MMM YYYY"),
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, "MMM YYYY", CommonDestinationTimezone).format("MMM YYYY"),
      filterParseDateFn: (v) => moment.tz(v as string, "MMM YYYY", CommonDestinationTimezone),
      aggregationFn: "firstChild" as any,
      minSize: 150,
      // cell: (info) => formatMonth(info.getValue() as string),
    },
    {
      id: "party_location",
      accessorKey: "party_location",
      header: "Party Location",
      accessorFn: (row) => row.party_location,
      aggregationFn: "firstChild" as any,
      minSize: 150,
      cell: (info) => info.getValue() as string,
    },
    {
      id: "total_granted",
      accessorKey: "total_granted",
      header: "Total Granted",
      accessorFn: (row) => row.was_granted,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "total_requested",
      accessorKey: "total_requested",
      header: "Total Requested",
      accessorFn: (row) => row.was_requested,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "amount_granted_numeric",
      accessorKey: "amount_granted_numeric",
      header: "Amount Granted",
      accessorFn: (row) => row.amount_granted_numeric,
      aggregationFn: sumDecimalAggregation,
      cell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
    },
  ];

  const partnerColumns: DataTableColumn<MonthByPartnerRow>[] = [
    {
      id: "month",
      accessorKey: "month",
      header: "Month",
      sortingFn: dateWithFormatSort("MMM YYYY"),
      accessorFn: (row) => moment.tz(row.month as string, CommonDestinationTimezone).format("MMM YYYY"),
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("MMM YYYY"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      aggregationFn: "firstChild" as any,
      minSize: 150,
      // cell: (info) => formatMonth(info.getValue() as string),
    },
    {
      id: "nominating_partner",
      accessorKey: "nominating_partner",
      header: "Nominating Partner",
      accessorFn: (row) => row.nominating_partner,
      aggregationFn: "firstChild" as any,
      cell: (info) => info.getValue() as string,
      minSize: 150,
    },
    {
      id: "total_granted",
      accessorKey: "total_granted",
      header: "Total Granted",
      accessorFn: (row) => row.was_granted,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "total_requested",
      accessorKey: "total_requested",
      header: "Total Requested",
      accessorFn: (row) => row.was_requested,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "amount_granted_numeric",
      accessorKey: "amount_granted_numeric",
      header: "Amount Granted",
      accessorFn: (row) => row.amount_granted_numeric,
      aggregationFn: sumDecimalAggregation,
      cell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
    },
  ];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">By Month &amp; Party Location</h3>
        <div className="space-y-4">
          <DataTable
            tableClassName="max-h-[680px] overflow-y-scroll"
            columns={locationColumns}
            data={data}
            enableSorting
            enableFiltering
            enableGrouping
            defaultCondensed
            enableExport
            stickyHeaders
            groupBy={["month", "party_location"]}
            defaultSorting={[
                { id: "month", desc: true },
            ]}
            defaultHideLeafRows={true}
            exportFilename={() => ["month_by_location", moment().format("YYYYMMDD")].join("_")}
          />
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4">By Month &amp; Nominating Partner</h3>
        <div className="space-y-4">
          <DataTable
            tableClassName="max-h-[680px] overflow-y-scroll"
            columns={partnerColumns}
            data={data}
            enableSorting
            enableFiltering
            defaultCondensed
            enableGrouping
            enableExport
            stickyHeaders
            groupBy={["month", "nominating_partner"]}
            defaultSorting={[
                { id: "month", desc: true },
            ]}
            defaultHideLeafRows={true}
            exportFilename={() => ["month_by_partner", moment().format("YYYYMMDD")].join("_")}
          />
        </div>
      </Card>
    </div>
  );
}
